import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { fmtDate } from "@/lib/format";
import { Empty } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const CHECKS = {
  NO_SALE: ["Senza prezzo di vendita", "bg-amber-100 text-amber-900"], NO_PURCHASE: ["Senza prezzo di acquisto", "bg-slate-100 text-slate-700"],
  SALE_BELOW_COST: ["Vendita sotto costo", "bg-red-600 text-white"], SALE_BELOW_ALT: ["Vendita sotto un fornitore alternativo", "bg-red-100 text-red-800"],
};
const PAGE = 200;
const euro4 = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 4 });
const pct = (n) => (n != null ? `${n.toLocaleString("it-IT")}%` : "—");

const ImportDialog = ({ open, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [validFrom, setValidFrom] = useState("");
  const [rep, setRep] = useState(null);
  const run = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("valid_from", validFrom);
    try { const { data } = await api.post("/pricelist/import", fd); setRep(data); onDone(); toast.success("Listino importato"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setRep(null); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="pricelist-import-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Importa listino acquisto e vendita</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Stesso formato del listino di maggio 2026: riga con i nomi dei fornitori sopra le colonne "prezzo di acquisto", poi Articolo, Descrizione, unità di misura, prezzo di vendita. Gli articoli vengono aggiornati in base al codice; i prezzi precedenti restano nello storico.</p>
        <label className="micro-label">Valido dal</label>
        <Input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} data-testid="pricelist-import-date" />
        <Input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files[0])} data-testid="pricelist-import-file" />
        <Button disabled={!file || !validFrom} onClick={run} data-testid="pricelist-import-submit">Importa</Button>
        {rep && <div className="border bg-muted/40 p-3 text-sm" data-testid="pricelist-import-report">{rep.items_created} articoli nuovi · {rep.items_updated} aggiornati · {rep.suppliers} fornitori ({rep.suppliers_created} nuovi) · {rep.to_check} da verificare</div>}
      </DialogContent>
    </Dialog>
  );
};

const History = ({ item, onClose }) => {
  const { nameOf } = useData();
  const [rows, setRows] = useState([]);
  useEffect(() => { if (item) api.get(`/pricelist/items/${item.id}/history`).then((r) => setRows(r.data)).catch(() => {}); }, [item]);
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="pricelist-history-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{item?.code} · {item?.name}</DialogTitle></DialogHeader>
        {rows.length === 0 ? <Empty text="Nessuno storico prezzi" /> : rows.map((h) => (
          <div key={h.id} className="border-b py-2 text-sm">
            <div className="flex justify-between"><strong>Valido dal {fmtDate(h.valid_from)}</strong><span>Vendita <span className="font-mono">{euro4(h.sale_price)}</span></span></div>
            {h.supplier_prices.map((p, i) => <div key={p.supplier_id} className="flex justify-between text-xs text-muted-foreground"><span>{i === 0 ? "★ " : ""}{nameOf("parties", p.supplier_id)}</span><span className="font-mono">{euro4(p.price)}</span></div>)}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export const PriceListTab = ({ onChanged }) => {
  const { can } = useAuth();
  const { nameOf, reload } = useData();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [chk, setChk] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [importOpen, setImportOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const load = useCallback(() => api.get("/pricelist").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  const cats = useMemo(() => [...new Set(items.map((i) => i.category).filter(Boolean))].sort(), [items]);
  const shown = useMemo(() => items.filter((i) => (!cat || i.category === cat) && (!chk || (chk === "ANY" ? i.checks.length : i.checks.includes(chk)))
    && (!q || `${i.code} ${i.name}`.toLowerCase().includes(q.toLowerCase()))), [items, q, cat, chk]);
  const toCheck = items.filter((i) => i.checks.length).length;
  return (
    <div data-testid="pricelist-tab">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Input className="w-64 bg-white" placeholder="Cerca codice o descrizione" value={q} onChange={(e) => { setQ(e.target.value); setLimit(PAGE); }} data-testid="pricelist-search" />
        <Select value={cat || "all"} onValueChange={(v) => setCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-60 bg-white" data-testid="pricelist-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte le categorie</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={chk || "all"} onValueChange={(v) => setChk(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64 bg-white" data-testid="pricelist-check-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli articoli</SelectItem><SelectItem value="ANY">Da verificare ({toCheck})</SelectItem>{Object.entries(CHECKS).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground" data-testid="pricelist-count">{shown.length} articoli</span>
        {can("inventory:write") && <Button className="ml-auto" variant="outline" onClick={() => setImportOpen(true)} data-testid="pricelist-import-button"><Upload className="mr-1 h-4 w-4" />Importa listino</Button>}
      </div>
      {shown.length === 0 ? <Empty text="Nessun articolo" testId="pricelist-empty" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50"><TableHead>Codice</TableHead><TableHead>Descrizione</TableHead><TableHead>Categoria</TableHead><TableHead>U.M.</TableHead><TableHead>Fornitore predefinito</TableHead><TableHead className="text-right">Acquisto</TableHead><TableHead>Alternative</TableHead><TableHead className="text-right">Vendita</TableHead><TableHead className="text-right">Ricarico</TableHead><TableHead className="text-right">Margine</TableHead><TableHead>Verifica</TableHead></TableRow></TableHeader>
            <TableBody>
              {shown.slice(0, limit).map((i) => (
                <TableRow key={i.id} className="cursor-pointer" onClick={() => setHist(i)} data-testid={`pricelist-row-${i.code}`}>
                  <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{i.code}</TableCell>
                  <TableCell className="text-sm">{i.name}</TableCell>
                  <TableCell className="text-xs">{i.category}</TableCell>
                  <TableCell className="text-xs">{i.unit}</TableCell>
                  <TableCell className="text-xs">{i.supplier_id ? nameOf("parties", i.supplier_id) : "—"}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{i.unit_cost ? euro4(i.unit_cost) : "—"}</TableCell>
                  <TableCell className="text-[11px] text-muted-foreground">{(i.supplier_prices || []).slice(1).map((p) => `${nameOf("parties", p.supplier_id)} ${euro4(p.price)}`).join(" · ") || "—"}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{i.sale_price ? euro4(i.sale_price) : "—"}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${i.markup_pct < 0 ? "text-red-700" : ""}`}>{pct(i.markup_pct)}</TableCell>
                  <TableCell className={`text-right font-mono text-xs ${i.margin_pct < 0 ? "text-red-700" : ""}`}>{pct(i.margin_pct)}</TableCell>
                  <TableCell><div className="flex flex-wrap gap-1">{i.checks.map((c) => <span key={c} className={`whitespace-nowrap px-1.5 py-0.5 text-[10px] font-semibold uppercase ${CHECKS[c][1]}`}>{CHECKS[c][0]}</span>)}</div></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {shown.length > limit && <Button variant="outline" className="mt-3" onClick={() => setLimit((l) => l + PAGE)} data-testid="pricelist-more">Mostra altri {Math.min(PAGE, shown.length - limit)}</Button>}
      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { load(); reload(); onChanged(); }} />
      <History item={hist} onClose={() => setHist(null)} />
    </div>
  );
};
